import React, { createContext, useContext, useState, useCallback } from 'react'
import { lensPath, lensProp, set, view, split, merge } from 'ramda'
import PropTypes from 'prop-types'

import { handleValidationError, validateWithSchema } from 'services/yup'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const DEFAULT_SEPARATOR = '.'

// --------------- 𝕌𝕥𝕚𝕝𝕤 ---------------

/**
 * @typedef TFormContext
 * @property {any} values
 * @property {any} errors
 * @property {boolean} readOnly
 * @property {(newData: any) => void} setData
 * @property {(newData: any) => void} setValues
 * @property {(newData: any) => void} setErrors
 * @property {(errorPath: string, value: string) => void} setFieldError
 * @property {(fieldPath: string, splitter: string) => void} getFieldValue
 * @property {(fieldPath: string, newValue: any, splitter: string) => void} setFieldValue
 * @property {(separator: string) => (event: import('react').ChangeEvent) => void} handleChange
 * @property {() => void} reset
 */

/** @type {import('react').Context<TFormContext>}*/
export const FormContext = createContext({ values: {} })
FormContext.displayName = 'FormContext'

export const useCurrentForm = () => useContext(FormContext)

export const useForm = ({ readOnly = false, initialValues = {} } = {}) => {
  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({})

  const handleChange = (separator) => (event) => {
    if (!readOnly) {
      const {
        target: { name, value },
      } = event

      setFieldValue(name, value, separator)
    }
  }

  const getFieldValue = (fieldPath, separator = DEFAULT_SEPARATOR) =>
    view(lensPath(split(separator, fieldPath)), values)

  const setData = (newData) => setValues((previous) => merge(previous, newData))

  const setFieldValue = (fieldPath, newValue, splitter = DEFAULT_SEPARATOR) => {
    const fieldLens = lensPath(split(splitter, fieldPath))
    setValues(set(fieldLens, newValue))
  }

  const setFieldError = (errorPath, value) => {
    const fieldLens = lensProp(errorPath)
    setErrors(set(fieldLens, value))
  }

  const reset = useCallback(() => {
    setValues(initialValues)
    setErrors({})
  }, [initialValues])

  const getValues = useCallback(() => {
    return { ...values }
  }, [values])

  return {
    values,
    errors,
    readOnly,
    setData,
    getValues,
    setValues,
    setErrors,
    setFieldError,
    getFieldValue,
    setFieldValue,
    handleChange,
    reset,
  }
}

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export function FormProvider({ form, children }) {
  return <FormContext.Provider value={form}>{children}</FormContext.Provider>
}

export function Form({
  formRef,
  children,
  onSubmit,
  initialData,
  schema,
  ...props
}) {
  const form = useForm({ initialValues: initialData })

  function handleSubmit(event, values, { reset }) {
    event.preventDefault()
    if (schema) {
      form.setErrors({})
      validateWithSchema(
        schema,
        values,
        (values) => onSubmit(values, { reset }, event),
        handleValidationError(form)
      )
    } else onSubmit(values, { reset }, event)
  }

  return (
    <FormProvider form={form}>
      <FormContext.Consumer>
        {({ values, reset } = {}) => (
          <form
            ref={(node) =>
              formRef
                ? (formRef.current = {
                    ...form,
                    FormComponent: node,
                  })
                : null
            }
            onSubmit={(e) => handleSubmit(e, values, { reset })}
            {...props}
          >
            {children}
          </form>
        )}
      </FormContext.Consumer>
    </FormProvider>
  )
}

Form.propTypes = {
  schema: PropTypes.object,
  initialData: PropTypes.object,
  formRef: PropTypes.exact({
    current: PropTypes.any,
  }).isRequired,
  onSubmit: PropTypes.func,
}

export * from './components'
export { FormRow } from './styles'
